import { CellRange, DataMap } from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridCellTemplate, FlexGridColumn } from '@grapecity/wijmo.react.grid';
import { FlexGridFilter } from '@grapecity/wijmo.react.grid.filter';
import TextComponent from 'components/Input/TextComponent';
import { ProcessLableComponent } from 'components/Input/ProcessLableComponent';
import { useAuth } from 'context/AuthContext';
import { useMaster } from 'context/MasterContext';
import { useRestApi } from 'context/RestApiContext';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { FlexGridXlsxConverter } from '@grapecity/wijmo.grid.xlsx';
import SelectComponent from 'components/Input/SelectComponent';
import { getFirstDayOfLastMonth, getLastDayOfThisMonth } from 'utils/dateUtils';
import { getAge, getAgeCategory } from 'utils/ageUtils';

import ConstructionProgressEditModal from 'views/ConstructionRequestList/ConstructionProgressEditModal';

import {
  ClientTypes,
  ProjectTypes,
  KoujiKbn1,
  KoujiKbn2,
  VisitReason,
  Areas,
  AgeCategoryWithClientTypes,
} from 'constants/constants';

// 工事
const ConstructionRequestList = (props) => {
  const auth = useAuth();
  const restApi = useRestApi();
  const location = useLocation();
  // 履歴
  const navigate = useNavigate();
  // グリッド
  const flexGrid = useRef();

  // 工事一覧
  const [constructionRequests, setConstructionRequests] = useState([]);

  // 選択中の工事No
  const [targetKoujiNo, setTargetKoujiNo] = useState();

  // 一覧の件数
  const [count, setCount] = useState(0);

  // 一覧の全件数
  const [total, setTotal] = useState(0);

  // 工事状況モーダルへのパラメタ
  const [constructionProgressEditModalParam, setConstructionProgressEditModalParam] = useState({});

  // 顧客検索条件
  const [searchCondition, setSearchCondition] = useState({
    ACTUAL_DATE_START: getFirstDayOfLastMonth(new Date()),
    ACTUAL_DATE_END: getLastDayOfThisMonth(new Date()),
    TEL: '',
    NAME: '',
    ADDRESS: '',
  });

  // マスタ情報
  const master = useMaster();

  // 生年月日を年齢に変換
  const toAgeDisplay = (birthday) => {
    const age = getAge(birthday);
    return age >= 0 ? `${age}歳` : '';
  };

  // 年間管理で絞るかチェック
  const [isAnnualManagement, setIsAnnualManagement] = useState(false);
  // 完了した工事を含めるかチェック
  const [isIncludeCompleted, setIsIncludeCompleted] = useState(true);
  // 顧客区分公共のみで絞るかチェック
  const [isClientTypePublicOnly, setIsClientTypePublicOnly] = useState(false);

  // 検索処理
  const SearchClients = (condition, targetKoujiNo = null) => {
    // 年間管理の条件を追加
    const searchCondition = {
      ...condition,
      IS_ANNUAL_MANAGEMENT: isAnnualManagement,
      IS_INCLUDE_COMPLETED: isIncludeCompleted,
      IS_CLIENT_TYPE_PUBLIC_ONLY: isClientTypePublicOnly,
    };
    restApi.post('/api/constructionRequest/list', searchCondition, (data) => {
      const _quoteRequests = data.baseConstruction.map((quoteRequest) => {
        return {
          ...quoteRequest,
          // 年代を取得
          MITSU_KEI: calculateDaysDifference(quoteRequest.KEI_DATE, quoteRequest.UKE_DATE),
          KEI_HIKI: calculateDaysDifference(quoteRequest.KEI_DATE, quoteRequest.HIKI_DATE),
          AGE: toAgeDisplay(quoteRequest.BIRTHDAY),
          AGE_CATEGORY: getAgeCategory(null, quoteRequest.CLIENT_TYPE, quoteRequest.AGE_CATEGORY),
          ACTUAL_PROFIT: calculateProfit(quoteRequest.FINAL_AMOUNT, quoteRequest.SEIKYU_GENKA),
          GENKA_RATE: calculateGenkaRate(quoteRequest.FINAL_AMOUNT, quoteRequest.SEIKYU_GENKA),
          INSPECTION_FLG_DISP: quoteRequest.INSPECTION_FLG == 1 ? '〇' : '',
          ACTUAL_KINGAKU: calculateActualKingaku(quoteRequest),
        };
      });
      setTotal(data.total);
      setConstructionRequests(_quoteRequests);
      setTargetKoujiNo(targetKoujiNo);
    });
  };

  // 初期表示処理
  useEffect(() => {
    // 別画面から戻ってきた場合は検索条件を復活
    let condition = location?.state?.searchCondition ?? {
      ACTUAL_DATE_START: getFirstDayOfLastMonth(new Date()),
      ACTUAL_DATE_END: getLastDayOfThisMonth(new Date()),
      TEL: '',
      NAME: '',
      ADDRESS: '',
    };
    setSearchCondition(condition);
    // 検索
    SearchClients(condition, location?.state?.targetClientCd);
  }, []);

  // 他の画面から戻ってきた場合に顧客を選択
  useEffect(() => {
    selectTarget(targetKoujiNo);
  }, [targetKoujiNo]);

  const selectTarget = (targetKoujiNo) => {
    if (targetKoujiNo) {
      const grid = flexGrid.current.control;

      // 対象のSEIKYU_NOを持つ行のインデックスを探す
      const index = grid.rows.findIndex((row) => row.dataItem?.SEIKYU_NO === targetKoujiNo);

      if (index >= 0) {
        setTimeout(() => {
          grid.select(new CellRange(index), true); // 対象行を選択状態にする
        }, 10);
      }
    }
  };

  // コード翻訳用のマッピング
  const planerWithEmpty = [...master.planer, { id: 0, name: '' }];
  const planerMap = new DataMap(planerWithEmpty, 'id', 'name');
  var projectTypesMap = new DataMap(ProjectTypes, 'key', 'value');
  var clientTypeMap = new DataMap(ClientTypes, 'key', 'value');
  var koujiKbn1Map = new DataMap(KoujiKbn1, 'key', 'value');
  var koujiKbn2Map = new DataMap(KoujiKbn2, 'key', 'value');
  var ukeTypeMap = new DataMap(master.ukeType, 'id', 'name');
  var visitReasonMap = new DataMap(VisitReason, 'key', 'value');
  var areaMap = new DataMap(Areas, 'key', 'value');
  var ageCategoryMap = new DataMap(AgeCategoryWithClientTypes, 'key', 'value');
  var constructionStatusMap = new DataMap(master.constructionStatus, 'id', 'name');
  var robotTypesMap = new DataMap(
    [{ ROBOT_TYPE: 0, ROBOT_TYPE_NAME: '' }, ...master.robotTypes],
    'ROBOT_TYPE',
    'ROBOT_TYPE_NAME'
  );

  // 顧客情報ボタン
  const handleOnClickClientInfo = useCallback(() => {
    navigate('/admin/ClientInfo', {
      state: {
        clientId: flexGrid.current.control.selectedItems[0]['CLIENT_CD'],
        seikyuNo: flexGrid.current.control.selectedItems[0]['SEIKYU_NO'],
      },
    });
  }, [navigate]);

  // 検索ボタン
  const handleOnClickSearch = () => {
    SearchClients(searchCondition);
  };

  // 編集処理
  const handleChange = (prop) => (target) => {
    setSearchCondition({ ...searchCondition, [prop]: target?.value ?? target?.target.value });
  };

  // Excelダウンロード
  const handleExcelDownload = () => {
    const grid = flexGrid.current.control; // FlexGridコンポーネントへの参照を取得
    const fileName = '工事一覧.xlsx'; // ダウンロードするExcelファイルの名前

    // Excelファイルを生成し、ダウンロードする
    FlexGridXlsxConverter.save(
      grid,
      {
        includeColumns: function (column) {
          return column.header; // ヘッダー名称なしは含めない（ボタンの項目）
        },
      },
      fileName
    );
  };

  // 工事状況編集ボタン
  const handleOnClickStatusEdit = () => {
    const grid = flexGrid.current.control;
    const collectionView = grid.collectionView;

    // 工事状況編集モーダルの呼び出し処理
    setConstructionProgressEditModalParam({
      call_list: true,
      constructionData: grid.selectedItems[0], // 現在選択されている行のデータを渡す
      // 工事状況編集後のコールバック関数
      callback: (construction) => {
        // ステータスを数値型に変換
        const updatedQuoteRequest = {
          ...construction,
          CONSTRUCTION_STATUS: Number(construction.CONSTRUCTION_STATUS),
          MITSU_KEI: calculateDaysDifference(construction.KEI_DATE, construction.UKE_DATE),
          KEI_HIKI: calculateDaysDifference(construction.KEI_DATE, construction.HIKI_DATE),
          ACTUAL_PROFIT: calculateProfit(construction.FINAL_AMOUNT, construction.SEIKYU_GENKA),
          GENKA_RATE: calculateGenkaRate(construction.FINAL_AMOUNT, construction.SEIKYU_GENKA),
          INSPECTION_FLG_DISP: construction.INSPECTION_FLG == 1 ? '〇' : '',
          ACTUAL_KINGAKU: calculateActualKingaku(construction),
        };

        // SEIKYU_NOが一致する行を検索
        const target = collectionView.items.find(
          (item) => item.SEIKYU_NO === construction.SEIKYU_NO
        );

        if (target) {
          // 対象行を直接上書き
          Object.assign(target, updatedQuoteRequest);

          //　再描画して変更を反映
          collectionView.refresh();

          // 編集した行を再選択
          selectTarget(construction.SEIKYU_NO);
        }
      },
    });
  };

  function calculateDaysDifference(dateStr1, dateStr2) {
    if (dateStr1 == null || dateStr2 == null) {
      return '';
    }
    // 文字列をDateオブジェクトに変換
    const date1 = new Date(dateStr1.replace(/\//g, '-'));
    const date2 = new Date(dateStr2.replace(/\//g, '-'));

    // 差をミリ秒単位で計算
    const timeDifference = Math.abs(date2 - date1);

    // ミリ秒を日数に変換
    const daysDifference = timeDifference / (1000 * 3600 * 24);

    return daysDifference + '日';
  }

  // 利益計算
  function calculateProfit(seikyu, genka) {
    if (!seikyu || !genka) return '';
    return Number(seikyu) - Number(genka);
  }

  // 原価率計算
  function calculateGenkaRate(seikyu, genka) {
    if (!seikyu || !genka || Number(seikyu) === 0) return '';
    const rate = Number(genka) / Number(seikyu);
    return (rate * 100).toFixed(1) + '%';
  }

  // 日付範囲判定
  const dateInRange = (d, start, end) => {
    if (!d || !start || !end) return false;
    const toDate = (x) => {
      // "yyyy/mm/dd" 対応
      const v = typeof x === 'string' ? x.replace(/\//g, '-') : x;
      const dt = new Date(v);
      return isNaN(dt) ? null : dt;
    };
    const dd = toDate(d), ss = toDate(start), ee = toDate(end);
    if (!dd || !ss || !ee) return false;
    return dd >= ss && dd <= ee;
  };

  const toNum = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const calculateActualKingaku = (quoteRequest) => {
    const { ACTUAL_DATE_START, ACTUAL_DATE_END } = searchCondition;
    let total = 0;

    if (dateInRange(quoteRequest.KEI_DATE, ACTUAL_DATE_START, ACTUAL_DATE_END)) {
      total += toNum(quoteRequest.ZEINUKI_KIN);
    }

    const inHiki = dateInRange(quoteRequest.HIKI_DATE, ACTUAL_DATE_START, ACTUAL_DATE_END);
    const inLost = dateInRange(quoteRequest.LOST_ORDER_DATE, ACTUAL_DATE_START, ACTUAL_DATE_END);
    if (inHiki || inLost) {
      total += toNum(quoteRequest.SEIKYU_KINGAKU);
    }

    return total;
  };

  // フィルタの変更処理
  const filterApplied = (s, e) => {
    let grid = s.grid;
    setCount(grid?.collectionView?.items.length);
  };

  // フォームのsubmit処理
  const handleOnSubmitSearch = (e) => {
    e.preventDefault();
    handleOnClickSearch();
  };

  return (
    <>
      <Card className="m-0 table-container">
        <Card.Body>
          <Row>
            <Col>
              <form onSubmit={handleOnSubmitSearch}>
                <Row>
                  <Col className="pl-3 pr-2" md="1.5" style={{ width: '200px' }}>
                    <Form.Group>
                      <label>顧客名</label>
                      <TextComponent
                        placeholder="部分一致で検索"
                        type="text"
                        value={searchCondition.NAME}
                        onChange={handleChange('NAME')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="1.5" style={{ width: '200px' }}>
                    <Form.Group>
                      <label>住所</label>
                      <TextComponent
                        placeholder="部分一致で検索"
                        type="text"
                        value={searchCondition.ADDRESS}
                        onChange={handleChange('ADDRESS')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="1.5" style={{ width: '160px' }}>
                    <Form.Group>
                      <label>電話番号</label>
                      <TextComponent
                        placeholder="部分一致で検索"
                        type="text"
                        value={searchCondition.TEL}
                        onChange={handleChange('TEL')}
                      ></TextComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="2.5">
                    <Form.Group>
                      <label>実績日</label>
                      <div className="d-flex" style={{ width: '340px' }}>
                        <TextComponent
                          type="date"
                          value={searchCondition.ACTUAL_DATE_START}
                          onChange={handleChange('ACTUAL_DATE_START')}
                        ></TextComponent>
                        <span className="mt-2 ml-2 mr-2">～</span>
                        <TextComponent
                          type="date"
                          value={searchCondition.ACTUAL_DATE_END}
                          onChange={handleChange('ACTUAL_DATE_END')}
                        ></TextComponent>
                      </div>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="1.5" style={{ width: '200px' }}>
                    <Form.Group>
                      <label>営業担当</label>
                      <SelectComponent
                        value={searchCondition.SALES_TAN_CD}
                        onChange={handleChange('SALES_TAN_CD')}
                      >
                        <option value={''}></option>
                        {master.salesStaff.map((x, i) => (
                          <option key={i} value={x['id']}>
                            {x['name']}
                          </option>
                        ))}
                      </SelectComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pr-2" md="1.5" style={{ width: '200px' }}>
                    <Form.Group>
                      <label>現場担当</label>
                      <SelectComponent
                        value={searchCondition.GENBA_TAN_CD}
                        onChange={handleChange('GENBA_TAN_CD')}
                      >
                        <option value={''}></option>
                        {master.genbaStaff.map((x, i) => (
                          <option key={i} value={x['id']}>
                            {x['name']}
                          </option>
                        ))}
                      </SelectComponent>
                    </Form.Group>
                  </Col>
                  <Col className="pl-2 pr-2" md="1.5">
                    <Form.Group>
                      <label style={{ fontSize: '14pt', color: 'black' }}>
                        <input
                          className="mr-2"
                          style={{ transform: 'scale(1.5)' }}
                          type="checkbox"
                          checked={isAnnualManagement}
                          onChange={() => setIsAnnualManagement(!isAnnualManagement)}
                        ></input>
                        年間管理のみ表示
                      </label>
                      <br />
                      <label style={{ fontSize: '14pt', color: 'black' }}>
                        <input
                          className="mr-2"
                          style={{ transform: 'scale(1.5)' }}
                          type="checkbox"
                          checked={isIncludeCompleted}
                          onChange={() => setIsIncludeCompleted(!isIncludeCompleted)}
                        ></input>
                        完了・失注の工事を含める
                      </label>
                      <br />
                      <label style={{ fontSize: '14pt', color: 'black' }}>
                        <input
                          className="mr-2"
                          style={{ transform: 'scale(1.5)' }}
                          type="checkbox"
                          checked={isClientTypePublicOnly}
                          onChange={() => setIsClientTypePublicOnly(!isClientTypePublicOnly)}
                        ></input>
                        公共で絞る
                      </label>
                    </Form.Group>
                  </Col>
                  <Col className="mt-1">
                    <Form.Group>
                      <br />
                      <Button
                        variant="success"
                        className="btn-fill"
                        size="sm"
                        type="submit"
                      >
                        検索
                      </Button>
                    </Form.Group>
                  </Col>
                </Row>
              </form>
            </Col>
          </Row>
          <Row>
            <Col>
              <Button
                variant="success"
                className="btn-fill m-2"
                size="sm"
                onClick={handleExcelDownload}
              >
                Excelダウンロード
              </Button>
              件数：{count}件 / 全{total}件
            </Col>
          </Row>
          <Row className="mt-2">
            <Col>
              <div className="table-scroll" style={{ height: 'calc(100vh - 290px)' }}>
                <FlexGrid
                  ref={flexGrid}
                  isReadOnly={true}
                  itemsSource={constructionRequests}
                  selectionMode={'Row'}
                  headersVisibility={'Column'}
                  style={{ height: '100%' }}
                  frozenColumns={6}
                  loadedRows={(grid) => {
                    grid.rows.forEach((ele) => {
                      ele.height = 38;
                    });
                  }}
                >
                  <FlexGridFilter filterApplied={filterApplied} />
                  <FlexGridColumn header="" binding="" isReadOnly={true} width={84}>
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <span>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="success"
                            title="顧客情報"
                            onClick={handleOnClickClientInfo}
                          >
                            person
                          </Button>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="dark"
                            title="工事状況編集"
                            onClick={handleOnClickStatusEdit}
                          >
                            more_horiz
                          </Button>
                        </span>
                      )}
                    />
                  </FlexGridColumn>
                  <FlexGridColumn
                    binding="CONTRACT_NUMBER"
                    header="契約番号"
                    align="right"
                    width={82}
                  ></FlexGridColumn>
                  <FlexGridColumn binding="KEI_DATE" header="契約日" width={82}></FlexGridColumn>
                  <FlexGridColumn
                    binding="CONSTRUCTION_STATUS"
                    header="ステータス"
                    width={82}
                    dataMap={constructionStatusMap}
                  ></FlexGridColumn>
                  <FlexGridColumn binding="NAME_1" header="顧客名1" width={180}></FlexGridColumn>
                  <FlexGridColumn binding="POST_CODE" header="〒"></FlexGridColumn>
                  <FlexGridColumn binding="FULL_ADDRESS" header="住所" width={200}></FlexGridColumn>
                  <FlexGridColumn
                    binding="ZEINUKI_KIN"
                    header="契約金額(税抜)"
                    width={120}
                    dataType="Number"
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="SEIKYU_KINGAKU"
                    header="金額変更(税抜)"
                    dataType="Number"
                    width={120}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="FINAL_AMOUNT"
                    header="最終金額(税抜)"
                    width={120}
                    dataType="Number"
                  ></FlexGridColumn>
                  <FlexGridColumn binding="KOUJI_MEI" header="工事名"></FlexGridColumn>
                  <FlexGridColumn
                    binding="ROBOT_TYPE"
                    dataMap={robotTypesMap}
                    header="ﾛﾎﾞｯﾄ工事種類"
                  ></FlexGridColumn>
                  <FlexGridColumn binding="ROBOT_MAKER" header="ﾛﾎﾞｯﾄ機種名"></FlexGridColumn>
                  <FlexGridColumn binding="TEL1" header="TEL1"></FlexGridColumn>
                  <FlexGridColumn binding="TEL2" header="TEL2"></FlexGridColumn>
                  <FlexGridColumn
                    binding="PROJECT_TYPE"
                    header="区分①"
                    width={60}
                    allowResizing={false}
                    dataMap={projectTypesMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="CLIENT_TYPE"
                    header="区分②"
                    width={60}
                    allowResizing={false}
                    dataMap={clientTypeMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="KOUJI_KBN1"
                    header="区分③"
                    width={60}
                    allowResizing={false}
                    dataMap={koujiKbn1Map}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="KOUJI_KBN2"
                    header="区分④"
                    width={80}
                    allowResizing={false}
                    dataMap={koujiKbn2Map}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="SALES_TAN_CD"
                    header="営業"
                    width={80}
                    allowResizing={false}
                    dataMap={planerMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="GENBA_TAN_CD"
                    header="現場担当"
                    allowResizing={false}
                    dataMap={planerMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="UKE_DATE"
                    header="見積依頼日"
                    width={82}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="MITSU_KEI"
                    header="相談⇒契約"
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="KOUKI_ST_ACT"
                    header="着工日"
                    allowResizing={false}
                    width={82}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="HIKI_DATE"
                    header="引渡し日"
                    allowResizing={false}
                    width={82}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="LOST_ORDER_DATE"
                    header="失注日"
                    allowResizing={false}
                    width={82}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="KEI_HIKI"
                    header="契約⇒引渡"
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="ACTUAL_PROFIT"
                    header="実績利益(税抜)"
                    align="right"
                    width={120}
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="SEIKYU_GENKA"
                    header="原価金額(税抜)"
                    align="right"
                    width={120}
                    allowResizing={false}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="GENKA_RATE"
                    header="原価率"
                    allowResizing={false}
                    align="right"
                    format="p1"
                    width={70}
                  ></FlexGridColumn>
                  <FlexGridColumn binding="BIKO" header="備考" width={200}></FlexGridColumn>
                  <FlexGridColumn
                    binding="INSPECTION_FLG_DISP"
                    header="アフター実施"
                    width={100}
                    align="center"
                  ></FlexGridColumn>
                  <FlexGridColumn binding="AGE" header="年齢"></FlexGridColumn>
                  <FlexGridColumn
                    binding="UKE_TYPE"
                    header="受付方法"
                    width={130}
                    allowResizing={false}
                    dataMap={ukeTypeMap}
                  ></FlexGridColumn>
                  <FlexGridColumn
                    binding="VISIT_REASON"
                    header="来店きっかけ"
                    width={200}
                    allowResizing={false}
                    dataMap={visitReasonMap}
                  >
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => {
                        const reason = visitReasonMap.getDisplayValue(cell.item.VISIT_REASON) || '';
                        const biko = cell.item.VISIT_REASON_BIKO || '';
                        return <span>{biko ? `${reason}（${biko}）` : reason}</span>;
                      }}
                    />
                  </FlexGridColumn>
                  <FlexGridColumn
                    binding="AGE_CATEGORY"
                    dataMap={ageCategoryMap}
                    header="年代(登録時)"
                  ></FlexGridColumn>
                  <FlexGridColumn binding="AREA" header="地域" dataMap={areaMap}></FlexGridColumn>
                  <FlexGridColumn binding="NAME_KANA_1" header="フリガナ"></FlexGridColumn>
                  <FlexGridColumn
                    binding="REQUEST_NUMBER"
                    align="right"
                    header="受付No"
                  ></FlexGridColumn>
                  <FlexGridColumn binding="REPORT_DATE" header="報告日"></FlexGridColumn>
                  <FlexGridColumn
                    binding="ACTUAL_KINGAKU"
                    align="right"
                    width={130}
                    header="実績金額(税抜)"
                  ></FlexGridColumn>
                  {/* <FlexGridColumn header="" binding="" isReadOnly={true} width={80}>
                    <FlexGridCellTemplate
                      cellType="Cell"
                      template={(cell) => (
                        <span>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="primary"
                            title="編集"
                            onClick={handleOnClickEditClient}
                          >
                            edit
                          </Button>
                          <Button
                            className="material-symbols-rounded cell-icon btn-fill"
                            variant="danger"
                            title="削除"
                            onClick={handleOnClickDeleteClient}
                          >
                            delete
                          </Button>
                        </span>
                      )}
                    />
                  </FlexGridColumn>
                  */}
                </FlexGrid>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>
      {/** 工事状況モーダル */}
      <ConstructionProgressEditModal params={constructionProgressEditModalParam} />
    </>
  );
};

export default ConstructionRequestList;
